// One theme for every chart in the reporting dashboard. The wrappers in this
// directory all read it, so a panel looks the same wherever it appears: one
// font, one gridline, one tooltip, one palette. A chart that invented its own
// colours would read as a different instrument.
//
// The palette is Okabe–Ito: eight hues chosen to stay distinguishable under
// protanopia, deuteranopia and tritanopia. Colour is never the only cue — every
// chart carries a legend and/or its own axis labels, and the dashboard's
// captions name the measure again in words — but a chart whose slices cannot be
// told apart is unreadable for a colourblind operator whatever the labels say.
import type { ChartDataset, TooltipItem } from "chart.js";

/** Okabe–Ito categorical palette, in the standard order. */
export const OKABE_ITO: readonly string[] = [
	"#0072B2", // blue
	"#D55E00", // vermillion
	"#009E73", // bluish green
	"#CC79A7", // reddish purple
	"#E69F00", // orange
	"#56B4E9", // sky blue
	"#F0E442", // yellow
	"#000000", // black
];

/** The colour of a mark that cannot be drilled into. The ledger's enum filters
 * cannot select the empty value, so a bucket keyed "" ("Unrecorded",
 * "Unassigned") is rendered plainly — and, muted, the chart shows at a glance
 * which marks are interactive. Muted, not grey-by-accident: this is the one
 * grey the dashboard uses to mean "no clause here". */
export const MUTED_COLOR = "#adb5bd";

/** Gridlines: present enough to read a value against, faint enough that the
 * data is the ink. */
export const GRID_COLOR = "rgba(0, 0, 0, 0.08)";

/** The axis labels' colour — darker than the gridlines, so the ticks read as
 * text rather than as more chart furniture. */
export const TICK_COLOR = "#495057";

/** The chart text is smaller than body copy: a panel is a summary, and its
 * labels must not compete with the figures. */
export const TICK_FONT = {
	family: "system-ui, -apple-system, 'Segoe UI', sans-serif",
	size: 11,
};

/** The palette colour at `index`, wrapping for series longer than the palette
 * (a 15-slice doughnut cycles the hues). */
export function sliceColor(index: number): string {
	return OKABE_ITO[index % OKABE_ITO.length];
}

/** `hex` at `alpha`, as `rgba()`. Chart fills want a translucent variant of the
 * line's own hue rather than a second colour. */
export function withAlpha(hex: string, alpha: number): string {
	const value = Number.parseInt(hex.slice(1), 16);
	const red = (value >> 16) & 0xff;
	const green = (value >> 8) & 0xff;
	const blue = value & 0xff;
	return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/** Counts are whole things; no chart in the dashboard labels a fraction of a
 * treatment or a catch. */
export function formatCount(value: number): string {
	return value.toLocaleString();
}

/** Amounts (area, applied quantity) are measured, so one decimal is the most
 * precision the display can honestly carry. */
export function formatAmount(value: number): string {
	return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/** The shared legend: below the plot, so it never steals width from a category
 * axis; hidden for a single unnamed series, where the axis is the label. */
export function legendOptions(display: boolean) {
	return {
		display,
		position: "bottom" as const,
		labels: {
			boxWidth: 10,
			boxHeight: 10,
			padding: 8,
			color: TICK_COLOR,
			font: TICK_FONT,
		},
	};
}

/** A bar dataset may carry the per-bucket areas beside its counts: the tooltip
 * then reads the count AND the area, which is the ledger's own pairing (a
 * treatment count says how often, the area says how much). The unit travels
 * separately, from the response, so it is never guessed from the numbers. */
export type BarDataset = ChartDataset<"bar", number[]> & {
	areas?: (number | null)[];
};

/** A line drawn over a bar chart: a range band's edges or its mean. The
 * `band` marker keeps the band's edges out of the tooltip — they are drawn to
 * show a range, not to be read as two more values. */
export type BarOverlayDataset = ChartDataset<"line", (number | null)[]> & {
	band?: boolean;
};

/** The tooltip label for a bar: the dataset's name and its value, plus the
 * bucket's area when the dataset carries one and the caller named the unit.
 *
 * A floating bar — one drawn as a `[from, to]` pair so a panel can stack
 * segments without asking the scale to stack, which would also stack the line
 * overlays — is read as its own length, not as the pair's upper edge. */
export function barTooltipLabel(
	context: TooltipItem<"bar" | "line">,
	horizontal: boolean,
	areaUnit?: string,
	suffix = "",
): string {
	const raw = context.raw;
	const value: number = Array.isArray(raw)
		? Math.abs(Number(raw[1] ?? 0) - Number(raw[0] ?? 0))
		: Number((horizontal ? context.parsed.x : context.parsed.y) ?? 0);
	const name = context.dataset.label ? `${context.dataset.label}: ` : "";
	const base = suffix
		? `${name}${formatAmount(value)}${suffix}`
		: `${name}${formatCount(value)}`;
	const area = (context.dataset as BarDataset).areas?.[context.dataIndex];
	if (area == null || !areaUnit) return base;
	return `${base} · ${formatAmount(area)} ${areaUnit}`;
}
